import { zodResolver } from '@hookform/resolvers/zod';
import {
  Action,
  Card,
  ConfirmModal,
  Disclosure,
  Icon,
  Modal,
  MultiSelect,
  MultiSelectOption,
  Page,
  RelativeDateTime,
  SimpleSummary,
  Skeleton,
  TextInput,
} from 'corso-admin-ui';
import { isTruthy, Pegasus } from 'corso-types';
import { FormEventHandler, ReactNode, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import DescriptionList, { Description } from '~/components/DescriptionList';
import EmptyStateAction from '~/components/EmptyStateAction';
import { useAutomationsForEventType } from '~/features/settings/hooks/useAutomations';
import { useProductCollections } from '~/features/settings/hooks/useProductCollections';
import {
  useProductGroupDelete,
  useProductGroupRefresh,
  useProductGroups,
  useProductGroupUpsert,
} from '~/features/settings/hooks/useProductGroups';
import { useProductTags } from '~/features/settings/hooks/useProductTags';
import { useProductTypes } from '~/features/settings/hooks/useProductTypes';
import { useToast } from '~/hooks/useToast';
import {
  ProductGroup,
  ProductGroupCreate,
  productGroupCreateSchema,
  productGroupSchema,
  UpsertFormProps,
} from '~/types';
import { useProductSync } from './hooks/useProductSync';
import ProductGroupProducts from './ProductGroupProducts';

// ? possibly elevate into `MultiSelect` for sharing
const multiSelectTransform = {
  toOptions: (options: { id: number; name: string }[]) =>
    options.map(
      ({ id, name }) =>
        ({ label: name, value: `${id}` }) satisfies MultiSelectOption,
    ),
  fromSelected: (
    options: { id: number; name: string }[],
    selected: MultiSelectOption[],
  ) =>
    selected
      .map((selection) =>
        options.find(
          (option) => option.id === Number.parseInt(selection.value, 10),
        ),
      )
      .filter(isTruthy),
};

function useProductGroupAutomationNames(productGroupId: number) {
  const registrationRules = useAutomationsForEventType(
    Pegasus.EventType.productRegistrationSelection,
  );

  const registrationInUse = registrationRules.filter(
    ({ rule: { event } }) =>
      event.type === 'productRegistrationSelection' &&
      event.params.productRegistrationProductGroupId === productGroupId,
  );

  const exchangeProductGroupRules = useAutomationsForEventType(
    Pegasus.EventType.offerAdvancedExchange,
  );

  const exchangeInUse = exchangeProductGroupRules.filter(
    ({ rule: { event } }) =>
      event.type === 'offerAdvancedExchange' &&
      event.params.exchangeKind === 'product' &&
      event.params.storeProductGroupIds.includes(productGroupId),
  );

  return [
    registrationInUse.map((r) => r.name),
    exchangeInUse.map((r) => r.name),
  ].flat();
}

function ProductGroupForm({
  show,
  onClose,
  onSubmit,
  values,
}: UpsertFormProps<ProductGroupCreate, ProductGroup>) {
  const update = !!values?.id;
  const { data: tags = [] } = useProductTags();

  const { data: types = [] } = useProductTypes();

  const { data: collections = [] } = useProductCollections();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    register,
  } = useForm({
    resolver: zodResolver(productGroupSchema.or(productGroupCreateSchema)),
    defaultValues: {
      tags: [],
      types: [],
      collections: [],
    },
    values,
  });

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((formValues) => {
      onSubmit(formValues);
      reset();
    })(event).catch(console.error);
  };

  return (
    <Modal
      title="Product Group"
      description="Create a group of products by selecting any number of tags and types."
      show={show}
      onClose={onClose}
    >
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <TextInput
          id="name"
          label="Name"
          details="The name of the product group in CREW, this name will be used when setting up other parts of the application."
          required
          {...register('name')}
          error={errors.name?.message}
        />

        <Controller
          name="tags"
          control={control}
          render={({ field: { onChange, value } }) => (
            <MultiSelect
              togglable
              label="Product tags"
              placeholder="Search for a tag"
              options={multiSelectTransform.toOptions(tags)}
              value={multiSelectTransform.toOptions(value)}
              onChange={(selected) =>
                onChange(multiSelectTransform.fromSelected(tags, selected))
              }
              details="Product tags to include in this group."
              error={errors.tags?.message}
            />
          )}
        />

        <Controller
          name="types"
          control={control}
          render={({ field: { onChange, value } }) => (
            <MultiSelect
              togglable
              label="Product types"
              placeholder="Search for a product type"
              options={multiSelectTransform.toOptions(types)}
              value={multiSelectTransform.toOptions(value)}
              onChange={(selected) =>
                onChange(multiSelectTransform.fromSelected(types, selected))
              }
              details="Product types to include in this group."
              error={errors.types?.message}
            />
          )}
        />
        <Controller
          name="collections"
          control={control}
          render={({ field: { onChange, value } }) => (
            <MultiSelect
              togglable
              label="Product collections"
              placeholder="Search for a product collection"
              options={multiSelectTransform.toOptions(collections)}
              value={multiSelectTransform.toOptions(value)}
              onChange={(selected) =>
                onChange(
                  multiSelectTransform.fromSelected(collections, selected),
                )
              }
              details="Product collections to include in this group."
              error={errors.types?.message}
            />
          )}
        />
        <Action variant="primary" type="submit">
          {update ? 'Save' : 'Create'} Product Group
        </Action>
      </form>
    </Modal>
  );
}

function ProductGroupDisplay({
  group: { name, types, tags, collections, id, createdOn },
  actions,
}: {
  group: ProductGroup;
  actions?: ReactNode;
}) {
  const inUseNames = useProductGroupAutomationNames(id);

  const descriptions: Description[] = [];

  if (tags.length > 0)
    descriptions.push({
      term: 'Tags',
      details: tags.map((t) => t.name).join(', '),
    });
  if (types.length > 0)
    descriptions.push({
      term: 'Types',
      details: types.map((t) => t.name).join(', '),
    });

  if (collections.length > 0)
    descriptions.push({
      term: 'Collections',
      details: collections.map((c) => c.name).join(', '),
    });

  if (inUseNames.length > 0)
    descriptions.push({
      term: 'Automations',
      details: inUseNames.join(', '),
    });

  return (
    <Disclosure
      renderSummary={
        <SimpleSummary>
          <div className="flex w-full flex-row items-center justify-between gap-2">
            <div className="flex flex-col items-start gap-1">
              <p className="truncate text-left text-sm font-medium text-balance text-neutral-700">
                {name}
              </p>

              {createdOn && (
                <p className="text-xs text-neutral-500">
                  Created <RelativeDateTime dateTime={createdOn} />
                </p>
              )}
            </div>
            {actions}
          </div>
        </SimpleSummary>
      }
    >
      <div className="mt-1 px-2">
        <DescriptionList descriptions={descriptions} />
      </div>
    </Disclosure>
  );
}

function ProductGroupSkeleton() {
  return (
    <div className="flex gap-2">
      <Skeleton.Rectangle height="36px" width="100%" />
      <Skeleton.Rectangle height="36px" width="44px" />
      <Skeleton.Rectangle height="36px" width="44px" />
      <Skeleton.Rectangle height="36px" width="44px" />
    </div>
  );
}

function RefreshProducts({
  groupName,
  groupId,
}: {
  groupName: string;
  groupId: number;
}) {
  const { isPending, mutate: refreshProducts } =
    useProductGroupRefresh(groupId);
  return (
    <Action
      icon="refresh"
      accessibilityLabel={`Refresh Products In ${groupName}`}
      onClick={() => refreshProducts()}
      loading={isPending}
      variant="ghost"
    />
  );
}

function DeleteGroup({ group }: { group: ProductGroup }) {
  const { mutate: deleteProductGroup, isPending } = useProductGroupDelete();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const confirmDelete = () => {
    deleteProductGroup(group);
    setShowConfirmation(false);
  };

  const automationNames = useProductGroupAutomationNames(group.id);
  const hasAutomations = automationNames.length > 0;

  return (
    <>
      <Action
        disabled={hasAutomations}
        icon="remove"
        accessibilityLabel={`Delete ${group.name} group`}
        onClick={() => setShowConfirmation(true)}
        variant="ghost"
      />

      <ConfirmModal
        title="Delete Group"
        prompt={`Are you sure you want to delete ${group.name}?`}
        confirmText="Delete"
        cancelText="Cancel"
        show={showConfirmation}
        loading={isPending}
        onConfirm={confirmDelete}
        onCancel={() => setShowConfirmation(false)}
      />
    </>
  );
}

function EditGroup({ group }: { group: ProductGroup }) {
  const { mutate: upsertProductGroup } = useProductGroupUpsert();
  const [showForm, setShowForm] = useState(false);

  return (
    <>
      <Action
        icon="edit"
        accessibilityLabel={`Edit ${group.name} group`}
        onClick={() => setShowForm(true)}
        variant="ghost"
      />

      <ProductGroupForm
        show={showForm}
        onClose={() => setShowForm(false)}
        onSubmit={(upsert) => {
          upsertProductGroup(upsert);
          setShowForm(false);
        }}
        values={group}
      />
    </>
  );
}

export default function ProductGroupSettingsPage() {
  const { data: groups, isLoading } = useProductGroups();
  const [showForm, setShowForm] = useState(false);
  const { mutate: saveGroup } = useProductGroupUpsert();
  const { mutate: syncProducts, isPending } = useProductSync();

  const sortedGroupsNewestFirst = groups.sort((a, b) => a.id - b.id).reverse();

  const toast = useToast();
  return (
    <Page
      title="Product Groups"
      primaryAction={{
        onAction: () => setShowForm(true),
        content: 'Add Group',
        icon: 'add',
      }}
      secondaryActions={[
        {
          id: 'sync-products',
          onAction: () => {
            toast.show(
              'Product sync has started. This may take up to 15 minutes to complete.',
            );

            syncProducts();
          },
          content: 'Sync Products From Shopify',
          icon: 'refresh',
          disabled: isPending,
        },
      ]}
    >
      <Card>
        <Skeleton
          instances={1}
          skeleton={ProductGroupSkeleton}
          isLoading={isLoading}
        >
          {sortedGroupsNewestFirst.length === 0 ? (
            <EmptyStateAction.Button
              onClick={() => setShowForm(true)}
              icon={<Icon icon="productGroup" />}
              label="Create a product group"
              description="Product groups are used to group products together for easier management."
            />
          ) : (
            <ul className="flex flex-col gap-2">
              {sortedGroupsNewestFirst.map((group) => (
                <li key={group.id}>
                  <ProductGroupDisplay
                    group={group}
                    actions={
                      <div className="flex flex-row gap-2">
                        <ProductGroupProducts group={group} key={group.id} />

                        <RefreshProducts
                          groupName={group.name}
                          groupId={group.id}
                        />

                        <EditGroup group={group} key={group.id} />
                        <DeleteGroup group={group} key={group.id} />
                      </div>
                    }
                  />
                </li>
              ))}
            </ul>
          )}
          <ProductGroupForm
            show={showForm}
            onClose={() => setShowForm(false)}
            onSubmit={(group) => {
              saveGroup(group);
              setShowForm(false);
            }}
          />
        </Skeleton>
      </Card>
    </Page>
  );
}
